import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { httpsCallable, getFunctions } from 'firebase/functions';

export interface ScrapingJob {
  id: string;
  searchQuery: string;
  location: string;
  source: 'google_maps' | 'yelp' | 'both';
  filters?: {
    minReviews?: number;
    maxLocations?: number;
    excludeChains?: boolean;
    priceTier?: string[];
  };
  status: 'pending' | 'running' | 'completed' | 'failed';
  resultsCount?: number;
  error?: string;
  createdBy: string;
  createdAt: Date;
  completedAt?: Date;
}

export class ScrapingJobService {
  /**
   * Create a new scraping job
   */
  static async createScrapingJob(
    jobData: Omit<ScrapingJob, 'id' | 'status' | 'createdAt' | 'resultsCount'>
  ): Promise<string> {
    console.log('[ScrapingJobService] Creating scraping job:', jobData);
    
    try {
      const jobDocument = {
        ...jobData,
        status: 'pending' as const,
        resultsCount: 0,
        createdAt: Timestamp.now(),
      };
      
      console.log('[ScrapingJobService] Adding document to Firestore...', jobDocument);
      const docRef = await addDoc(collection(db, 'scrapingJobs'), jobDocument);
      console.log('[ScrapingJobService] Scraping job created successfully:', { jobId: docRef.id });
      
      return docRef.id;
    } catch (error: any) {
      console.error('[ScrapingJobService] Error creating scraping job:', {
        error,
        errorMessage: error?.message,
        errorCode: error?.code,
        stack: error?.stack,
        jobData,
      });
      throw error;
    }
  }

  /**
   * Get scraping jobs
   */
  static async getScrapingJobs(userId?: string): Promise<ScrapingJob[]> {
    let q = query(
      collection(db, 'scrapingJobs'),
      orderBy('createdAt', 'desc')
    );

    if (userId) {
      q = query(q, where('createdBy', '==', userId));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      completedAt: doc.data().completedAt?.toDate(),
    })) as ScrapingJob[];
  }

  /**
   * Get scraping job by ID
   */
  static async getScrapingJob(id: string): Promise<ScrapingJob> {
    const docRef = doc(db, 'scrapingJobs', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Scraping job not found');
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      completedAt: data.completedAt?.toDate(),
    } as ScrapingJob;
  }

  /**
   * Trigger scraping job via Cloud Function
   */
  static async triggerScraping(
    jobId: string,
    searchQuery: string,
    location: string,
    source: 'google_maps' | 'yelp' | 'both',
    filters?: any
  ): Promise<void> {
    const requestPayload = {
      jobId,
      searchQuery,
      location,
      source,
      filters,
    };

    console.log('[ScrapingJobService] Triggering scraping job:', {
      jobId,
      searchQuery,
      location,
      source,
      filters,
      timestamp: new Date().toISOString(),
    });

    try {
      const functions = getFunctions();
      console.log('[ScrapingJobService] Functions instance created, getting callable function...');
      
      const scrapeRestaurants = httpsCallable(functions, 'scrapeRestaurants');
      console.log('[ScrapingJobService] Callable function obtained, making request...');
      
      const startTime = Date.now();
      const result = await scrapeRestaurants(requestPayload);
      const duration = Date.now() - startTime;
      
      console.log('[ScrapingJobService] Scraping job triggered successfully:', {
        jobId,
        result,
        duration: `${duration}ms`,
        timestamp: new Date().toISOString(),
      });
    } catch (error: any) {
      console.error('[ScrapingJobService] Error triggering scraping job:', {
        jobId,
        error: error,
        errorMessage: error?.message,
        errorCode: error?.code,
        errorDetails: error?.details,
        stack: error?.stack,
        requestPayload,
        timestamp: new Date().toISOString(),
      });

      // Extract more detailed error information
      let errorMessage = 'Unknown error occurred';
      
      if (error?.code) {
        errorMessage = `Error code: ${error.code}`;
      }
      
      if (error?.message) {
        errorMessage += ` - ${error.message}`;
      }
      
      if (error?.details) {
        console.error('[ScrapingJobService] Error details:', error.details);
        errorMessage += ` (Details: ${JSON.stringify(error.details)})`;
      }

      // Re-throw with enhanced error message
      const enhancedError = new Error(`Failed to trigger scraping job: ${errorMessage}`);
      (enhancedError as any).originalError = error;
      (enhancedError as any).jobId = jobId;
      throw enhancedError;
    }
  }

  /**
   * Cancel scraping job
   */
  static async cancelScrapingJob(id: string): Promise<void> {
    await updateDoc(doc(db, 'scrapingJobs', id), {
      status: 'failed',
      error: 'Cancelled by user',
    });
  }
}

